import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Grid, Card, CardContent, Button, ToggleButton, ToggleButtonGroup,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Divider, Alert,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from 'recharts';
import api from '../../services/api';

type Period = { calls: number; answered: number; minutes: number; cost: number; acd_sec: number; asr: number };
interface Data {
  scope: { carriers: string[] };
  periods: { today: Period; week: Period; month: Period; two_months: Period };
  projection: { daily: any; weekly: any; monthly: any };
  trend: { date: string; calls: number; minutes: number; cost: number }[];
  traffic_profile: {
    top_destinations: { prefix: string; calls: number; answered: number; minutes: number; cost: number; asr: number; acd_sec: number }[];
    by_hour: { hour: number; calls: number; minutes: number }[];
  };
  countries: { code: string; name: string; calls: number; answered: number; minutes: number; cost: number; asr: number; acd_sec: number }[];
  payment: { amount_due: number; currency: string; contacts: any[]; how_to_pay: string };
  generated_at: string;
}

const money = (n: number) => '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mmss = (s: number) => `${Math.floor((s || 0) / 60)}m ${String((s || 0) % 60).padStart(2, '0')}s`;
const num = (n: number) => (n || 0).toLocaleString();

const Kpi: React.FC<{ label: string; value: string; sub?: string; accent?: string }> = ({ label, value, sub, accent }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: .5 }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: .5, color: accent || 'text.primary' }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

export const BTMonitorPage: React.FC = () => {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'two_months'>('month');
  const [country, setCountry] = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr('');
    api.get('/carrier-monitor/dashboard', { params: { country: country || undefined } })
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [country]);
  useEffect(() => { load(); }, [load]);

  const p = data?.periods[period];
  const periodLabel = { today: 'Today', week: 'This Week', month: 'This Month', two_months: 'Last 2 Months' }[period];
  const scopeName = data?.scope.carriers.join(', ') || 'Carrier';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">{scopeName} — traffic, cost & settlement</Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180, mr: 1 }}>
          <InputLabel>Country</InputLabel>
          <Select value={country} label="Country" onChange={e => setCountry(e.target.value)}>
            <MenuItem value="">All countries</MenuItem>
            {(data?.countries || []).filter(c => c.code).map(c => (
              <MenuItem key={c.code} value={c.code}>{c.name} ({c.calls.toLocaleString()})</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined" size="small">Refresh</Button>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 10 }}><CircularProgress /></Box>}
      {err && <Alert severity="error">{err}</Alert>}

      {data && !loading && (
        <>
          <ToggleButtonGroup exclusive size="small" value={period} onChange={(_, v) => v && setPeriod(v)} sx={{ mb: 2 }}>
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="week">This Week</ToggleButton>
            <ToggleButton value="month">This Month</ToggleButton>
            <ToggleButton value="two_months">Last 2 Months</ToggleButton>
          </ToggleButtonGroup>

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} md={2.4}><Kpi label={`Calls · ${periodLabel}`} value={num(p!.calls)} sub={`${num(p!.answered)} answered`} /></Grid>
            <Grid item xs={6} md={2.4}><Kpi label="Total Minutes" value={num(p!.minutes)} sub="billed minutes" /></Grid>
            <Grid item xs={6} md={2.4}><Kpi label="Cost to BT" value={money(p!.cost)} sub="buy cost (what we owe)" accent="#6366F1" /></Grid>
            <Grid item xs={6} md={2.4}><Kpi label="ACD" value={mmss(p!.acd_sec)} sub="avg call duration" /></Grid>
            <Grid item xs={6} md={2.4}><Kpi label="ASR" value={`${p!.asr}%`} sub="answer-seizure ratio" /></Grid>
          </Grid>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Projected settlement (run-rate from last 7 days)</Typography>
              <Grid container spacing={2}>
                {(['daily', 'weekly', 'monthly'] as const).map(k => (
                  <Grid item xs={12} md={4} key={k}>
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{k} projection</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366F1' }}>{money(data.projection[k].cost)}</Typography>
                      <Typography variant="caption" color="text.secondary">{num(data.projection[k].calls)} calls · {num(data.projection[k].minutes)} min</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Traffic by country — last 60 days (click a row to filter)</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Country</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">Answered</TableCell>
                      <TableCell align="right">Minutes</TableCell>
                      <TableCell align="right">Cost to BT</TableCell>
                      <TableCell align="right">ASR</TableCell>
                      <TableCell align="right">ACD</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.countries.map((c, i) => (
                      <TableRow key={i} hover selected={country === c.code}
                        sx={{ cursor: c.code ? 'pointer' : 'default' }}
                        onClick={() => c.code && setCountry(country === c.code ? '' : c.code)}>
                        <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                        <TableCell align="right">{num(c.calls)}</TableCell>
                        <TableCell align="right">{num(c.answered)}</TableCell>
                        <TableCell align="right">{num(c.minutes)}</TableCell>
                        <TableCell align="right">{money(c.cost)}</TableCell>
                        <TableCell align="right">{c.asr}%</TableCell>
                        <TableCell align="right">{mmss(c.acd_sec)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Daily traffic & cost — last 60 days</Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={data.trend}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line yAxisId="l" type="monotone" dataKey="minutes" name="Minutes" stroke="#6366F1" dot={false} />
                      <Line yAxisId="r" type="monotone" dataKey="cost" name="Cost ($)" stroke="#10B981" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2">Amount due to BT</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#EF4444', my: 1 }}>{money(data.payment.amount_due)}</Typography>
                  <Typography variant="caption" color="text.secondary">Unbilled buy cost (not yet settled)</Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">How to pay</Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>{data.payment.how_to_pay}</Typography>
                  {data.payment.contacts.map((c, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                      {c.billing_contact_name && <Typography variant="caption" display="block">Billing: {c.billing_contact_name}</Typography>}
                      {c.billing_email && <Typography variant="caption" display="block">{c.billing_email}</Typography>}
                      {c.billing_phone && <Typography variant="caption" display="block">{c.billing_phone}</Typography>}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Traffic profile by hour (last 7 days)</Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.traffic_profile.by_hour}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="calls" name="Calls" fill="#6366F1" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Top destinations (last 60 days)</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Prefix</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">Minutes</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">ASR</TableCell>
                        <TableCell align="right">ACD</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.traffic_profile.top_destinations.map((d, i) => (
                        <TableRow key={i}>
                          <TableCell>{d.prefix}</TableCell>
                          <TableCell align="right">{num(d.calls)}</TableCell>
                          <TableCell align="right">{num(d.minutes)}</TableCell>
                          <TableCell align="right">{money(d.cost)}</TableCell>
                          <TableCell align="right">{d.asr}%</TableCell>
                          <TableCell align="right">{mmss(d.acd_sec)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Generated {new Date(data.generated_at).toLocaleString()} · Figures are BT carrier cost only. Customer pricing is not part of this view.
          </Typography>
        </>
      )}
    </Box>
  );
};
